import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useAuth } from "../contexts/AuthContext";
import logoIcon from "../assets/Logo-icon.svg";
import loginBanner from "../assets/login-banner.jpg";
import Button from "../components/UI/Button";
import Logo from "../assets/Logo.png";
import LoginForm from "../components/Forms/LoginForm";
import SignUpForm from "../components/Forms/SignUpForm";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

const LoginPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { login, signup, googleAuth, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Determine active tab based on current route
  const getActiveTabFromRoute = () => {
    const path = location.pathname;
    if (path === "/signup") return "signup";
    return "login"; // default to login for '/login' or any other path
  };

  const [activeTab, setActiveTab] = useState(getActiveTabFromRoute());

  // Update active tab when route changes
  useEffect(() => {
    const newActiveTab = getActiveTabFromRoute();
    if (newActiveTab !== activeTab) {
      setActiveTab(newActiveTab);
      reset(); // Clear form when route changes
      setShowPassword(false); // Reset password visibility
    }
  }, [location.pathname]);

  // Handle Google Sign-In response
  const handleGoogleResponse = useCallback(async (response) => {
    if (response.credential) {
      const result = await googleAuth(response.credential);
      if (result.success) {
        navigate("/dashboard");
      }
    }
  }, [googleAuth, navigate]);

  // Initialize Google Sign-In and render button
  useEffect(() => {
    if (window.google && GOOGLE_CLIENT_ID) {
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleResponse,
      });
      
      // Render the Google button in the container
      const buttonContainer = document.getElementById("google-signin-button");
      if (buttonContainer) {
        window.google.accounts.id.renderButton(buttonContainer, {
          type: "standard",
          theme: "filled_black",
          size: "large",
          text: activeTab === "login" ? "signin_with" : "signup_with",
          shape: "rectangular",
          width: 300,
        });
      }
    }
  }, [handleGoogleResponse, activeTab]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();

  const onSubmit = async (data) => {
    try {
      let result;
      if (activeTab === "login") {
        result = await login(data.email, data.password);
        if (result.success) {
          console.log("Login successful, redirecting to dashboard...");
          navigate("/dashboard");
        }
      } else {
        result = await signup(
          data.name,
          data.email,
          data.password,
          data.passwordConfirm
        );
        if (result.success) {
          console.log("Signup successful, redirecting to email verification...");
          navigate("/verify-email-required");
        }
      }
    } catch (error) {
      console.error("Authentication error:", error);
    }
  };

  const renderTabContent = () => {
    const formProps = {
      register,
      errors,
      showPassword,
      showConfirmPassword,
      setShowPassword,
      setShowConfirmPassword,
      loading,
    };

    switch (activeTab) {
      case "login":
        return <LoginForm {...formProps} />;
      case "signup":
        return <SignUpForm {...formProps} />;
      default:
        return null;
    }
  };

  const handleTabChange = (tab) => {
    // Navigate to the appropriate route
    if (tab === "signup") {
      navigate("/signup");
    } else {
      navigate("/login");
    }
    // The useEffect will handle updating the activeTab state and resetting the form
  };

  return (
    <div className="h-screen bg-white w-full flex overflow-hidden">
      <div className="hidden w-1/2 md:flex bg-ag-white-200">
        <img
          src={loginBanner}
          alt="Login Banner"
          className="w-full h-full object-cover"
        />
      </div>
      <div className="w-full md:w-1/2 px-4 md:px-20 flex flex-col items-center justify-center bg-black">
        <div className="">
          <img src={logoIcon} alt="logo" width={49} height={49} />
        </div>
        <div className="max-w-[550px] w-full mt-6 md:mt-0  rounded-3xl px-10 pt-8 pb-8 flex flex-col items-center justify-center">
          <p className="text-center font-bold text-4xl text-white">
            Welcome to Autograph
          </p>
          {/* Google Sign-In Button - rendered by Google's SDK */}
          <div id="google-signin-button" className="mt-4 flex justify-center mb-4"></div>

          <div className="bg-black rounded-xl p-1">
            <button
              onClick={() => handleTabChange("login")}
              className={`py-2 px-4 rounded-lg text-lg font-semibold ${
                activeTab === "login"
                  ? "bg-ag-black-750 text-white"
                  : "text-gray-650"
              }`}
            >
              Log in
            </button>
            <button
              onClick={() => handleTabChange("signup")}
              className={`py-2 px-4 rounded-lg text-lg font-semibold ${
                activeTab === "signup"
                  ? "bg-ag-black-750 text-white"
                  : "text-gray-650"
              }`}
            >
              Sign up
            </button>
          </div>

          <div className="w-full" onSubmit={handleSubmit(onSubmit)}>
            {renderTabContent()}

            <Button
              type="submit"
              size="lg"
              loading={loading}
              disabled={loading}
              onClick={handleSubmit(onSubmit)}
              className="w-full mt-4"
            >
              {activeTab === "login" ? "Log In" : "Create Account"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
